import {
  TRANSPOSED_LABEL_KEY,
  formatTableCell,
  type TableColumn,
  type TableModel,
  type TableRow,
} from './table-model';

/**
 * Troca linhas por colunas: a primeira coluna vira o cabeçalho e cada coluna restante
 * vira uma linha. Usa o texto de exibição dos rótulos, já que eles passam a ser cabeçalho.
 *
 * Rótulos repetidos ganham sufixo `(2)`, `(3)`… porque as chaves precisam ser únicas.
 */
export function transposeTable(model: TableModel, labelHeader = ''): TableModel {
  const [labelColumn, ...valueColumns] = model.columns;
  if (!labelColumn || !valueColumns.length || !model.rows.length) return model;

  const used = new Map<string, number>();
  const headers = model.rows.map((row) => {
    const base = formatTableCell(labelColumn, row) || '—';
    const seen = used.get(base) ?? 0;
    used.set(base, seen + 1);
    return seen === 0 ? base : `${base} (${seen + 1})`;
  });

  const columns: TableColumn[] = [
    { key: TRANSPOSED_LABEL_KEY, label: labelHeader || labelColumn.label },
    ...headers.map((header) => ({ key: header, label: header, numeric: true })),
  ];

  const rows: TableRow[] = valueColumns.map((column) => {
    const row: TableRow = { [TRANSPOSED_LABEL_KEY]: column.label };
    model.rows.forEach((sourceRow, index) => {
      // Aplica o `format` da coluna de origem aqui: depois de transpor, a coluna que
      // carregava a formatação virou linha e o dado seria exibido cru.
      row[headers[index]] = column.format
        ? column.format(sourceRow[column.key], sourceRow)
        : sourceRow[column.key];
    });
    return row;
  });

  return { columns, rows };
}

/** Prefixo das colunas geradas pelo cruzamento, para não colidir com as chaves originais. */
const PIVOT_PREFIX = 'pivot:';

/** Anos primeiro em ordem numérica; o resto alfabético com números respeitados. */
function comparePivotValues(a: string, b: string): number {
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return a.localeCompare(b, 'pt-BR', { numeric: true });
}

/**
 * Cruza a tabela: os valores de `pivotKey` viram colunas e `measureKey` preenche as células.
 * É o caminho para o formato longo (`ano | nível | valor`) virar uma matriz legível.
 *
 * Devolve `null` quando o cruzamento não é confiável — menos de dois valores para virar
 * coluna, nenhuma dimensão sobrando, ou mais de uma linha caindo na mesma célula. Nesse
 * último caso agregar seria inventar número, então a operação simplesmente não é oferecida.
 */
export function pivotTable(model: TableModel, pivotKey: string, measureKey: string): TableModel | null {
  const pivotColumn = model.columns.find((column) => column.key === pivotKey);
  const measureColumn = model.columns.find((column) => column.key === measureKey);
  if (!pivotColumn || !measureColumn || !model.rows.length) return null;

  const dimensions = model.columns.filter(
    (column) => column.key !== pivotKey && column.key !== measureKey,
  );
  if (!dimensions.length) return null;

  const pivotValues: string[] = [];
  for (const row of model.rows) {
    const value = formatTableCell(pivotColumn, row);
    if (!value) return null;
    if (!pivotValues.includes(value)) pivotValues.push(value);
  }
  if (pivotValues.length < 2) return null;
  pivotValues.sort(comparePivotValues);

  const byGroup = new Map<string, TableRow>();
  const order: string[] = [];

  for (const row of model.rows) {
    const groupKey = dimensions.map((column) => formatTableCell(column, row)).join('\u0000');
    let target = byGroup.get(groupKey);
    if (!target) {
      target = {};
      for (const column of dimensions) target[column.key] = row[column.key];
      byGroup.set(groupKey, target);
      order.push(groupKey);
    }
    const cellKey = PIVOT_PREFIX + formatTableCell(pivotColumn, row);
    if (cellKey in target) return null;
    target[cellKey] = row[measureKey];
  }

  const columns: TableColumn[] = [
    ...dimensions,
    ...pivotValues.map((value) => ({
      key: PIVOT_PREFIX + value,
      label: value,
      numeric: measureColumn.numeric,
      // Reconstrói a linha original para o `format` da medida continuar funcionando
      // (ele costuma olhar campos vizinhos, como `tipo_valor`).
      format: measureColumn.format
        ? (cell: unknown, row: TableRow) => measureColumn.format!(cell, { ...row, [measureKey]: cell })
        : undefined,
    })),
  ];

  return { columns, rows: order.map((key) => byGroup.get(key)!) };
}

/** Mantém apenas as colunas visíveis, na ordem pedida. Chaves desconhecidas são ignoradas. */
export function pickColumns(model: TableModel, keys: readonly string[]): TableModel {
  const byKey = new Map(model.columns.map((column) => [column.key, column]));
  const columns = keys.map((key) => byKey.get(key)).filter((column): column is TableColumn => !!column);
  if (!columns.length) return model;
  return { columns, rows: model.rows };
}

/** Move uma coluna uma posição para a esquerda ou direita, devolvendo a nova ordem de chaves. */
export function moveColumn(keys: readonly string[], key: string, direction: -1 | 1): string[] {
  const index = keys.indexOf(key);
  const target = index + direction;
  if (index === -1 || target < 0 || target >= keys.length) return [...keys];
  const next = [...keys];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}
